import React, { useMemo } from "react";
import {
  useDeleteRoyaltyReminder,
  useMyRoyaltyReminders,
  useUpdateRoyaltyReminder,
} from "@seabrookstudios/pitch2table";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { formatFullDate } from "../core/date-formatting";
import { useRoyaltyReminderIdFromRoute } from "./params";
import { SelectGamesDialog } from "../core/SelectGamesDialog";
import { RoyaltyReminderPublisherSelect } from "./RoyaltyReminderPublisherSelect";
import { EditableDate } from "../core/EditableDate";
import Stack from "@mui/material/Stack";
import { getNextOccurrenceDate } from "./model";
import CardActions from "@mui/material/CardActions";
import { Path } from "../Path";
import { useNavigate } from "react-router-dom";
import Delete from "@mui/icons-material/Delete";
import { CircularProgressSize } from "../theme";
import CircularProgress from "@mui/material/CircularProgress";

/** @type {{ label: string, value: import("@seabrookstudios/pitch2table-core").CycleType }[]} */
const cycleOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half-yearly", value: "twice-yearly" },
  { label: "Yearly", value: "yearly" },
];

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").CycleType} props.value
 * @param {(value: import("@seabrookstudios/pitch2table-core").CycleType) => void} props.onChange
 * @param {boolean} props.isBusy
 */
const CycleTypeSelector = ({ value, onChange, isBusy }) => {
  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Cycle type
      </Typography>
      <ButtonGroup variant="outlined" disableElevation fullWidth>
        {cycleOptions.map((option) => (
          <Button
            key={option.value}
            variant={value === option.value ? "contained" : "outlined"}
            onClick={() => onChange(option.value)}
            disabled={isBusy}
          >
            {option.label}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
};

export const RoyaltyReminderDetail = () => {
  const reminderId = useRoyaltyReminderIdFromRoute();
  const { data: reminders = [] } = useMyRoyaltyReminders();
  const updateReminder = useUpdateRoyaltyReminder(reminderId);
  const deleteReminder = useDeleteRoyaltyReminder(reminderId);
  const navigate = useNavigate();

  const reminder = useMemo(() => reminders.find((item) => item.id === reminderId), [reminders, reminderId]);
  const nextOccurrenceDate = useMemo(
    () => getNextOccurrenceDate(reminder?.startDate, reminder ? reminder.cycleType : "monthly"),
    [reminder?.cycleType, reminder?.startDate]
  );
  const displayDate = typeof nextOccurrenceDate === "number" ? nextOccurrenceDate : reminder?.startDate;

  if (!reminder) {
    return <EmptyDetailCard text="Select a royalty reminder from the list on the left." />;
  }

  /**
   * @param {number} date
   */
  const onUpdateStartDate = async (date) => {
    await updateReminder.mutateAsync({
      startDate: date,
      cycleType: undefined,
      publisherIds: undefined,
      gameIds: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").CycleType} cycleType
   */
  const onUpdateCycleType = async (cycleType) => {
    await updateReminder.mutateAsync({ cycleType, startDate: undefined, publisherIds: undefined, gameIds: undefined });
  };

  /**
   * @param {import("@seabrookstudios/types").GameId[]} gameIds
   */
  const saveGameIds = async (gameIds) => {
    await updateReminder.mutateAsync({ gameIds, startDate: undefined, cycleType: undefined, publisherIds: undefined });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} publisherIds
   */
  const savePublisherIds = async (publisherIds) => {
    await updateReminder.mutateAsync({ publisherIds, startDate: undefined, cycleType: undefined, gameIds: undefined });
  };

  const onDeleteReminder = async () => {
    await deleteReminder.mutateAsync();

    await navigate(Path.royalties().reminders());
  };

  return (
    <Card elevation={1} square={false}>
      <CardContent>
        <List>
          <ListItem>
            <CycleTypeSelector
              value={reminder.cycleType}
              onChange={onUpdateCycleType}
              isBusy={updateReminder.isPending}
            />
          </ListItem>

          <Stack direction="row" justifyContent="space-between">
            <ListItem>
              <EditableDate
                title="Start date"
                timestamp={reminder.startDate}
                onSave={(date) => {
                  onUpdateStartDate(date);
                }}
                isBusy={updateReminder.isPending}
              />
            </ListItem>
            <ListItem>
              {displayDate && <ListItemText primary="Next occurrence date" secondary={formatFullDate(displayDate)} />}
            </ListItem>
          </Stack>
          <SelectGamesDialog
            value={reminder.gameIds}
            onChange={saveGameIds}
            defaultSelectedStatuses={["Published", "Signed"]}
          />
          <RoyaltyReminderPublisherSelect
            gameIds={reminder.gameIds}
            value={reminder.publisherIds}
            onChange={savePublisherIds}
          />
        </List>
      </CardContent>
      <CardActions>
        <Button
          variant="outlined"
          color="error"
          onClick={onDeleteReminder}
          startIcon={deleteReminder.isPending ? <CircularProgress size={CircularProgressSize} /> : <Delete />}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};
